import { reactive, computed } from 'vue'
import axios from 'axios'

// Intervalle de vérification des nouvelles notifications (onglet visible)
const POLL_INTERVAL = 30000

// Catégories connues avant le chargement du catalogue (et repli si l'API est indisponible)
const FALLBACK_CATEGORY = { key: 'general', label: 'Général', icon: 'bi-bell', color: '#64748b', locked: true, events: [] }

// Global state for notifications
const state = reactive({
    toasts: [],
    notifications: [],
    unreadCount: 0,
    loading: false,
    initialized: false,
    catalog: [],
    lastId: 0,
    // Vrai dès que lastId reflète la dernière notification connue : seules les suivantes s'affichent en toast
    baselineSet: false,
    pollUsers: 0
})

let pollTimer = null
let catalogPromise = null
let checking = false
let lastCheckAt = 0

const normalize = (n) => ({
    ...n,
    notificationId: n.id,
    read: n.is_read,
    timestamp: n.created_at
})

const categoryMeta = (key) =>
    state.catalog.find(c => c.key === key) || (key === 'qhse'
        ? { ...FALLBACK_CATEGORY, key: 'qhse', label: 'QHSE', icon: 'bi-shield-check', color: '#22c55e' }
        : FALLBACK_CATEGORY)

export function useNotifications() {
    const loadCatalog = () => {
        if (!catalogPromise) {
            catalogPromise = axios.get('/api/notifications/catalog')
                .then(({ data }) => { state.catalog = data.categories || [] })
                .catch(error => {
                    catalogPromise = null
                    console.error('Error loading notification catalog:', error)
                })
        }
        return catalogPromise
    }

    // Fetch notifications from API
    const fetchNotifications = async () => {
        if (state.loading) return

        state.loading = true
        try {
            const response = await axios.get('/api/notifications', {
                params: { per_page: 50 }
            })

            state.notifications = (response.data.notifications?.data || []).map(normalize)
            state.unreadCount = response.data.unread_count || 0
            state.lastId = Math.max(state.lastId, ...state.notifications.map(n => n.id), 0)
            state.baselineSet = true
            state.initialized = true
        } catch (error) {
            console.error('Error fetching notifications:', error)
        } finally {
            state.loading = false
        }
    }

    // Initialize notifications on first use
    const initNotifications = () => {
        loadCatalog()
        if (!state.initialized && !state.loading) {
            fetchNotifications()
        }
    }

    // Nouvelles notifications depuis la dernière connue : badge mis à jour et toast pour chacune
    const checkForNew = async () => {
        if (document.hidden || !localStorage.getItem('token')) return
        // Focus, retour sur l'onglet et minuterie peuvent arriver ensemble : un seul appel à la fois
        if (checking || Date.now() - lastCheckAt < 5000) return
        checking = true
        lastCheckAt = Date.now()
        try {
            const { data } = await axios.get('/api/notifications/summary', {
                params: state.baselineSet ? { after_id: state.lastId } : {}
            })
            state.unreadCount = data.unread_count || 0

            const fresh = (data.new || [])
                .filter(n => !state.notifications.some(existing => existing.id === n.id))
                .map(normalize)

            if (state.baselineSet && fresh.length) {
                state.notifications = [...fresh, ...state.notifications].slice(0, 100)
                announce(fresh)
            }
            state.lastId = Math.max(state.lastId, data.latest_id || 0)
            state.baselineSet = true
        } catch (error) {
            // Silencieux : la prochaine vérification réessaiera
        } finally {
            checking = false
        }
    }

    const announce = (fresh) => {
        const shown = fresh.slice(0, 3)
        shown.forEach(n => {
            addToast({
                type: n.type,
                title: n.title,
                message: n.message,
                duration: n.priority === 'high' ? 12000 : 7000,
                notificationId: n.id,
                action: n.action_url ? { label: n.action_label || 'Ouvrir', to: n.action_url } : null,
                category: categoryMeta(n.category)
            })
        })
        if (fresh.length > shown.length) {
            addToast({
                type: 'info',
                title: 'Notifications',
                message: `${fresh.length - shown.length} autre(s) nouvelle(s) notification(s)`,
                action: { label: 'Tout voir', to: '/notifications' }
            })
        }
    }

    const onVisibilityChange = () => {
        if (!document.hidden) checkForNew()
    }

    // Démarré par la cloche ; plusieurs abonnés partagent la même minuterie
    const startPolling = () => {
        state.pollUsers++
        if (pollTimer) return
        pollTimer = setInterval(checkForNew, POLL_INTERVAL)
        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('focus', onVisibilityChange)
    }

    const stopPolling = () => {
        state.pollUsers = Math.max(0, state.pollUsers - 1)
        if (state.pollUsers > 0 || !pollTimer) return
        clearInterval(pollTimer)
        pollTimer = null
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.removeEventListener('focus', onVisibilityChange)
    }

    // Add a toast notification (local only - for immediate feedback)
    const addToast = (options) => {
        const toast = {
            id: Date.now() + Math.random(),
            type: options.type || 'info',
            title: options.title || '',
            message: options.message || '',
            duration: options.duration !== undefined ? options.duration : 5000,
            persistent: options.persistent || false,
            action: options.action || null,
            notificationId: options.notificationId || null,
            category: options.category || null,
            timestamp: new Date()
        }

        state.toasts.push(toast)

        // Auto remove after duration (if not persistent)
        if (toast.duration > 0 && !toast.persistent) {
            setTimeout(() => {
                removeToast(toast.id)
            }, toast.duration)
        }

        return toast.id
    }

    // Remove a toast notification
    const removeToast = (id) => {
        const index = state.toasts.findIndex(t => t.id === id)
        if (index > -1) {
            state.toasts.splice(index, 1)
        }
    }

    const setLocalRead = (id, read) => {
        const notification = state.notifications.find(n => n.notificationId === id || n.id === id)
        if (notification) {
            notification.read = read
            notification.is_read = read
        }
        return notification
    }

    // Mark notification as read (API call)
    const markAsRead = async (id) => {
        try {
            await axios.patch(`/api/notifications/${id}/read`)

            const notification = state.notifications.find(n => n.notificationId === id || n.id === id)
            const wasUnread = notification ? !notification.read : true
            setLocalRead(id, true)
            if (wasUnread) state.unreadCount = Math.max(0, state.unreadCount - 1)
        } catch (error) {
            console.error('Error marking notification as read:', error)
        }
    }

    // Mark notification as unread (API call)
    const markAsUnread = async (id) => {
        try {
            await axios.patch(`/api/notifications/${id}/unread`)

            setLocalRead(id, false)
            state.unreadCount++
        } catch (error) {
            console.error('Error marking notification as unread:', error)
        }
    }

    // Mark all as read (API call), optionally a single category
    const markAllAsRead = async (category = null) => {
        try {
            await axios.post('/api/notifications/mark-all-read', category ? { category } : {})

            state.notifications.forEach(n => {
                if (!category || n.category === category) {
                    n.read = true
                    n.is_read = true
                }
            })
            state.unreadCount = category ? state.notifications.filter(n => !n.read).length : 0
            if (category) fetchNotifications()
        } catch (error) {
            console.error('Error marking all as read:', error)
        }
    }

    // Actions groupées : 'read', 'unread', 'delete'
    const bulkAction = async (ids, action) => {
        if (!ids.length) return 0
        const { data } = await axios.post('/api/notifications/bulk', { ids, action })
        fetchNotifications()
        return data.affected || 0
    }

    // Remove notification (API call)
    const removeNotification = async (id) => {
        try {
            await axios.delete(`/api/notifications/${id}`)

            const index = state.notifications.findIndex(n => n.notificationId === id || n.id === id)
            if (index > -1) {
                const wasUnread = !state.notifications[index].read
                state.notifications.splice(index, 1)
                if (wasUnread) {
                    state.unreadCount = Math.max(0, state.unreadCount - 1)
                }
            }
        } catch (error) {
            console.error('Error removing notification:', error)
        }
    }

    // Clear all notifications (API call)
    const clearAllNotifications = async () => {
        try {
            await axios.delete('/api/notifications/clear-all')

            state.notifications = []
            state.unreadCount = 0
        } catch (error) {
            console.error('Error clearing notifications:', error)
        }
    }

    // Ouvre la page liée à une notification (et la marque lue)
    const openAction = (notification, router) => {
        if (!notification?.action_url) return false
        if (!(notification.is_read ?? notification.read)) markAsRead(notification.id)
        router.push(notification.action_url).catch(() => {})
        return true
    }

    // Préférences de l'utilisateur et ce que l'administration autorise par catégorie
    const fetchPreferences = async () => {
        const { data } = await axios.get('/api/notifications/preferences')
        return { preferences: data.preferences || {}, availability: data.availability || {} }
    }

    const savePreferences = async (preferences) => {
        const { data } = await axios.put('/api/notifications/preferences', { preferences })
        return data.preferences || preferences
    }

    // Convenience methods for different toast types
    const success = (message, options = {}) => {
        return addToast({
            type: 'success',
            message,
            ...options
        })
    }

    const error = (message, options = {}) => {
        return addToast({
            type: 'error',
            message,
            persistent: true,
            ...options
        })
    }

    const warning = (message, options = {}) => {
        return addToast({
            type: 'warning',
            message,
            ...options
        })
    }

    const info = (message, options = {}) => {
        return addToast({
            type: 'info',
            message,
            ...options
        })
    }

    // Computed properties
    const unreadCountComputed = computed(() => state.unreadCount)
    const hasUnread = computed(() => state.unreadCount > 0)

    return {
        // State
        toasts: computed(() => state.toasts),
        notifications: computed(() => state.notifications),
        unreadCount: unreadCountComputed,
        hasUnread,
        loading: computed(() => state.loading),
        catalog: computed(() => state.catalog),

        // Methods
        initNotifications,
        fetchNotifications,
        loadCatalog,
        categoryMeta,
        startPolling,
        stopPolling,
        checkForNew,
        addToast,
        removeToast,
        markAsRead,
        markAsUnread,
        markAllAsRead,
        bulkAction,
        removeNotification,
        clearAllNotifications,
        openAction,
        fetchPreferences,
        savePreferences,

        // Convenience methods
        success,
        error,
        warning,
        info
    }
}
